<div class="main-container">
  <div class="shipping-header">
    <div class="shipping-header-title">
      <h2 class="section-header dashboard-section-header">Shipping</h2>
      <span class="shipping-timezone">{{ getTimezone() }}</span>
    </div>
    <mat-form-field class="warehouse-field">
      <mat-label>Warehouse</mat-label>
      <mat-select
        [value]="defaultWarehouse()?.city"
        (selectionChange)="selectedWarehouse($event.value)"
      >
        @for (option of warehouseOptions; track option) {
          <mat-option [value]="option.city">
            {{ option.city }}, {{ option.state }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
  </div>

  <section class="content">
    <div class="shipping-panel">
      <!-- Delivery Vans -->
      <div class="shipping-panel-header">Delivery Vans</div>
      <div class="shipping-panel-body">
        @if (deliveryVanLoader()) {
          <spinner></spinner>
        } @else {
          @if (deliveryVans()?.length) {
            <div class="delivery-van-grid">
              <div class="delivery-van-row delivery-van-header-row">
                <strong>Type</strong>
                <strong>Cutoff</strong>
                <strong>Arrive</strong>
                <strong>Driver</strong>
              </div>
              @for (row of deliveryVans(); track $index) {
                <div class="delivery-van-row">
                  <div>
                    {{ row.deliveryPickup }}
                  </div>
                  <div
                    [matTooltip]="
                      row.isPickup
                        ? 'Pickups cannot be used for deliveries'
                        : ''
                    "
                    [ngClass]="row.isPickup ? 'underline' : ''"
                    matTooltipPosition="above"
                  >
                    {{
                      convertISOToPrettyTimeLocal(
                        row.cutOff,
                        platformAccount()?.address?.state!
                      )
                    }}
                  </div>
                  <div>{{ convertDateToPrettyDay(row.arrivalDate) }}</div>
                  <div class="driver-cell">
                    <div>{{ row.driver }}</div>
                    <div>{{ row.phone }}</div>
                  </div>
                </div>
              }
            </div>
          }
          @if (returnCutOffs()) {
            <div class="delivery-van-grid return-cutoff-grid">
              <div class="delivery-van-row">
                <div><strong>Return Cutoff</strong></div>
                <div>
                  <strong>Th</strong>&nbsp;<span>{{
                    getCutoffStatus(returnCutOffs()?.THURSDAY_RUN)
                  }}</span>
                </div>
                <div>
                  <strong>Fri</strong>&nbsp;<span>{{
                    getCutoffStatus(returnCutOffs()?.FRIDAY_RUN)
                  }}</span>
                </div>
                <div>
                  <strong>Sat</strong>&nbsp;<span>{{
                    getCutoffStatus(returnCutOffs()?.SATURDAY_RUN)
                  }}</span>
                </div>
              </div>
            </div>
          }
        }
      </div>
    </div>

    <!-- Ship To Zip -->
    <div class="shipping-panel">
      <div class="shipping-panel-header">
        Ship To Zip {{ platformAccount()?.address?.zip }}
      </div>
      <div class="shipping-panel-body">
        @if (parcelLoader()) {
          <spinner></spinner>
        } @else {
          @if (parcelInfo()?.length) {
            <div class="carrier-grid">
              <div class="carrier-row carrier-header-row">
                <strong>Service</strong>
                <strong class="align-right">UPS</strong>
                <strong class="align-right">FedEx</strong>
              </div>
              @for (row of groupedCarriersArray(); track $index) {
                <div class="carrier-row">
                  <strong>{{ row.label }}</strong>
                  <div class="align-right">
                    @if (row.ups) {
                      {{ convertISOToPrettyTime(row.ups.arrival) }}
                      ({{
                        getShortHandCarrierService(row.ups.carrierService)
                      }})
                    }
                  </div>
                  <div class="align-right">
                    @if (row.fedex) {
                      {{ convertISOToPrettyTime(row.fedex.arrival) }}
                      ({{
                        getShortHandCarrierService(row.fedex.carrierService)
                      }})
                    }
                  </div>
                </div>
              }
            </div>
          }
        }
      </div>
    </div>

    <!-- Delivery Day Grid -->
    <div class="shipping-panel">
      <div class="shipping-panel-header">
        Carriers &middot; Default {{ preferredCarrier() }}
      </div>
      <div class="shipping-panel-body">
        @if (deliveryDaysLoader()) {
          <spinner></spinner>
        } @else {
          @if (deliveryDays()?.length) {
            <div class="delivery-day-grid">
              <div class="delivery-day-row delivery-day-header-row">
                <span></span>
                <strong>Carrier Type</strong>
                <strong class="align-right">Closing In</strong>
              </div>
              <div class="delivery-day-row">
                <mat-icon
                  class="status"
                  [ngClass]="getCarrierShipStatus('UP', 'GROUND').colorClass"
                  [matTooltip]="getCarrierShipStatus('UP', 'GROUND').toolTip"
                  matTooltipPosition="above"
                  >{{ getCarrierShipStatus("UP", "GROUND").icon }}</mat-icon
                >
                <strong
                  >UPS Ground {{ getCarrierShipDays("UPS", "Ground") }}</strong
                >
                <div class="align-right">
                  {{ getCarrierShipStatus("UP", "GROUND").toolTip }}
                </div>
              </div>

              <div class="delivery-day-row">
                <mat-icon
                  class="status"
                  [ngClass]="getCarrierShipStatus('UP', 'AIR').colorClass"
                  [matTooltip]="getCarrierShipStatus('UP', 'AIR').toolTip"
                  matTooltipPosition="above"
                  >{{ getCarrierShipStatus("UP", "AIR").icon }}</mat-icon
                >
                <strong>UPS Air</strong>
                <div class="align-right">
                  {{ getCarrierShipStatus("UP", "AIR").toolTip }}
                </div>
              </div>

              <div class="delivery-day-row">
                <mat-icon
                  class="status"
                  [ngClass]="getCarrierShipStatus('FX', 'GROUND').colorClass"
                  [matTooltip]="getCarrierShipStatus('FX', 'GROUND').toolTip"
                  matTooltipPosition="above"
                  >{{ getCarrierShipStatus("FX", "GROUND").icon }}</mat-icon
                >
                <strong
                  >FedEX Ground
                  {{ getCarrierShipDays("Fedex", "Ground") }}</strong
                >
                <div class="align-right">
                  {{ getCarrierShipStatus("FX", "GROUND").toolTip }}
                </div>
              </div>

              <div class="delivery-day-row">
                <mat-icon
                  class="status"
                  [ngClass]="getCarrierShipStatus('FX', 'AIR').colorClass"
                  [matTooltip]="getCarrierShipStatus('FX', 'AIR').toolTip"
                  matTooltipPosition="above"
                  >{{ getCarrierShipStatus("FX", "AIR").icon }}</mat-icon
                >
                <strong>FedEX Air</strong>
                <div class="align-right">
                  {{ getCarrierShipStatus("FX", "AIR").toolTip }}
                </div>
              </div>
            </div>
          }
        }
      </div>
    </div>
  </section>
</div>
